import SocialAvatar from '@anm/components/SocialAvatar';
import Switch from '@anm/components/form/Switch';
import Image from '@anm/components/image/Image';
import noop from '@anm/helpers/noop';
import { useCallback, FC, MouseEvent } from 'react';

import {
  AccountListWrapper,
  AccountTitle,
  ConnectedLabel,
  DeleteButton,
  DestinationItemWrapper,
  DestinationRow,
  EditButton,
  Error,
  ReconnectButton,
  SwitchContainer,
  Title,
  TooltipWrapper
} from './Wrapper';
import { AccountsListProps, ItemProps } from './types';

export * from './types';

const DestinationItem: FC<ItemProps> = ({
  id,
  kind,
  title,
  avatar,
  network,
  isError,
  isChecked,
  isDisabled,
  isConnected,
  isDisconnected,
  showToggles = true,
  onEdit,
  onDelete,
  onConnect,
  onSelectDestination
}) => {
  const handleEditDestination = useCallback(
    (e: MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();
      onEdit?.(id);
    },
    [id, onEdit]
  );

  const handleReconnectDestination = useCallback(
    (e: MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();
      onConnect?.();
    },
    [id, onConnect]
  );

  const handelDelete = useCallback(() => {
    onDelete?.(id);
    onSelectDestination(id);
  }, [onDelete]);

  const canShowSwitch = typeof isConnected === 'undefined' && showToggles;
  const showEditButton = onEdit && !isDisconnected && isChecked && !isError;
  const canSelectDesctination = isDisconnected || isConnected || !showToggles || isDisabled;

  const handelRowClick = useCallback(canSelectDesctination ? noop : () => onSelectDestination(id), [
    canSelectDesctination,
    onSelectDestination
  ]);

  return (
    <DestinationRow isHoverStyle={!isConnected && !onDelete} onClick={handelRowClick}>
      <DestinationItemWrapper isDisconnected={isDisconnected}>
        <SocialAvatar alt={title} network={network} kind={kind} avatarSrc={avatar} variant="30x30">
          <Image src={avatar} alt={title} />
        </SocialAvatar>
        <AccountTitle>{title}</AccountTitle>
      </DestinationItemWrapper>
      {onDelete && <DeleteButton src="images/icons/trash.svg" alt="delete destination" onClick={handelDelete} />}
      {canShowSwitch && (
        <SwitchContainer>
          {isDisconnected && (
            <TooltipWrapper
              withArrow
              direction="to-top"
              variant="black"
              renderContent={() => (
                <span>
                  Wave.video live streaming studio has lost
                  <br />
                  access to this destination. But, no worries,
                  <br />
                  just click here to reconnect.
                </span>
              )}
            >
              <ReconnectButton onClick={handleReconnectDestination} />
            </TooltipWrapper>
          )}
          {showEditButton && <EditButton onClick={handleEditDestination} />}
          {isChecked && isError && <Error src="images/icons/error.svg" alt="error" />}
          <Switch key={isChecked + id} checked={isChecked} onChange={noop} />
        </SwitchContainer>
      )}
      {isConnected && <ConnectedLabel>Connected</ConnectedLabel>}
    </DestinationRow>
  );
};

const AccountsList: FC<AccountsListProps> = ({ list, title, subtitle, ...itemProps }) => {
  if (!list.length) return null;

  return (
    <AccountListWrapper>
      {title && <Title level={2} title={title} description={subtitle} />}
      {list.map(d => (
        <DestinationItem {...d} {...itemProps} key={d.id} />
      ))}
    </AccountListWrapper>
  );
};

export default AccountsList;
